import React, { useContext, useEffect, useState } from "react";
import { Card, Badge, Table, Button, Modal, Form } from "react-bootstrap";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import SubmitTaskApprovalDialog from "./SubmitTaskApprovalDialog";
import TaskApprovalDecisionDialog from "./TaskApprovalDecisionDialog";
import { toast } from "react-toastify";

// Phase 4B, §27. The task-detail approval panel — kept structurally and
// visually separate from task status/TAT/performers/delegation (Phase 4A),
// per the task spec's explicit instruction. Every action here operates
// through the approval APIs only; nothing here ever writes tasks.status
// directly or touches task_performers/task_delegations.
function requestStatusVariant(status) {
  switch (status) {
    case "PENDING": return "warning";
    case "APPROVED": return "success";
    case "REJECTED": return "danger";
    case "CHANGES_REQUESTED": return "info";
    case "CANCELLED": return "secondary";
    case "SUPERSEDED": return "light";
    default: return "light";
  }
}

export default function TaskApprovalPanel({ taskId, approvalSummary }) {
  const { user } = useContext(AuthContext) || { user: null };
  const [requests, setRequests] = useState([]);
  const [activeDetail, setActiveDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showSubmit, setShowSubmit] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState(null); // { requestId, stage }
  const [delegateTarget, setDelegateTarget] = useState(null);
  const [delegateToUserId, setDelegateToUserId] = useState("");
  const [delegateReason, setDelegateReason] = useState("");
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tasks/${taskId}/approval-requests`);
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRequests(list);
      const active = list.find((r) => r.status === "PENDING") || list[0];
      if (active) {
        const detailRes = await api.get(`/tasks/${taskId}/approval-requests/${active.id}`);
        setActiveDetail(detailRes.data?.data || null);
        setHistory(detailRes.data?.data?.history || []);
      } else {
        setActiveDetail(null);
        setHistory([]);
      }
    } catch (err) {
      console.error("Load task approvals error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const cancelRequest = async (request) => {
    const reason = window.prompt("Cancel this approval request? Enter a reason (optional):");
    if (reason === null) return;
    try {
      await api.post(`/tasks/${taskId}/approval-requests/${request.id}/cancel`, { reason: reason || undefined });
      toast.success("Approval request cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel approval request");
    }
  };

  const openDelegate = async (stage) => {
    setDelegateTarget(stage);
    setDelegateToUserId("");
    setDelegateReason("");
    if (!users.length) {
      const res = await api.get("/users").catch(() => null);
      setUsers(Array.isArray(res?.data) ? res.data : []);
    }
  };

  const submitDelegate = async () => {
    if (!delegateToUserId) { toast.warning("Select a delegate"); return; }
    try {
      await api.post(`/task-approval-requests/${activeDetail.id}/stages/${delegateTarget.id}/delegate`, {
        delegated_to_user_id: Number(delegateToUserId), reason: delegateReason || undefined,
      });
      toast.success("Approval stage delegated");
      setDelegateTarget(null);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TASK_APPROVAL_DELEGATE_INVALID") toast.error("You cannot delegate an approval stage to yourself, or the selected user is invalid.");
      else toast.error(data?.message || "Failed to delegate approval stage");
    }
  };

  const currentPendingStage = activeDetail?.status === "PENDING"
    ? activeDetail.stages?.find((s) => s.stage_order === activeDetail.current_stage_order)
    : null;
  const isMyDecision = !!(currentPendingStage && user?.id && (
    Number(currentPendingStage.delegated_to_user_id || currentPendingStage.resolved_approver_user_id) === Number(user.id)
  ));
  const isMyRequest = !!(activeDetail && user?.id && Number(activeDetail.requested_by) === Number(user.id));

  if (!loading && !requests.length && !approvalSummary?.can_submit_for_approval) {
    return null; // no policy has ever applied here — panel stays hidden, per "omit the optional structure"
  }

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">Task Approval</div>
          {approvalSummary?.can_submit_for_approval && (
            <Button size="sm" variant="outline-primary" onClick={() => setShowSubmit(true)}>Submit for Approval</Button>
          )}
        </div>

        {!requests.length ? (
          <div className="text-muted small">No approval has ever been requested for this task.</div>
        ) : (
          <>
            {activeDetail && (
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-2 small mb-2">
                  <Badge bg={requestStatusVariant(activeDetail.status)}>{activeDetail.status}</Badge>
                  <span>Policy: <strong>{activeDetail.policy_name}</strong></span>
                  <span>Requester: <strong>{activeDetail.requested_by_name}</strong></span>
                  <span>Requested: {activeDetail.requested_at ? new Date(activeDetail.requested_at).toLocaleString() : "—"}</span>
                </div>

                <Table size="sm" responsive>
                  <thead><tr><th>#</th><th>Stage</th><th>Status</th><th>Approver</th><th>Decision</th><th>Remarks</th><th></th></tr></thead>
                  <tbody>
                    {(activeDetail.stages || []).map((s) => (
                      <tr key={s.id}>
                        <td>{s.stage_order}</td>
                        <td>{s.stage_name}</td>
                        <td><Badge bg={requestStatusVariant(s.status)}>{s.status}</Badge></td>
                        <td className="small">{s.delegated_to_name || s.resolved_approver_name || "—"}{s.delegated_to_name ? " (delegated)" : ""}</td>
                        <td className="small">{s.decision || "—"}</td>
                        <td className="small">{s.remarks || "—"}</td>
                        <td>
                          {activeDetail.status === "PENDING" && s.stage_order === activeDetail.current_stage_order && (
                            <div className="d-flex gap-1">
                              {isMyDecision && <Button size="sm" variant="outline-primary" onClick={() => setDecisionTarget(s)}>Decide</Button>}
                              {isMyDecision && <Button size="sm" variant="outline-secondary" onClick={() => openDelegate(s)}>Delegate</Button>}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {activeDetail.status === "PENDING" && isMyRequest && (
                  <Button size="sm" variant="outline-danger" onClick={() => cancelRequest(activeDetail)}>Cancel Request</Button>
                )}
              </div>
            )}

            {history.length > 0 && (
              <>
                <strong className="small d-block mb-2">Decision History</strong>
                <div className="d-flex flex-column gap-1 small mb-3">
                  {history.map((h) => (
                    <div key={h.id}>
                      <span className="text-muted">{new Date(h.created_at).toLocaleString()}</span> — <Badge bg="light" text="dark">{h.event_type}</Badge> {h.actor_name ? `by ${h.actor_name}` : ""} {h.remarks ? `— ${h.remarks}` : ""}
                    </div>
                  ))}
                </div>
              </>
            )}

            {requests.length > 1 && (
              <>
                <strong className="small d-block mb-2">Previous Requests</strong>
                <Table size="sm" responsive>
                  <thead><tr><th>#</th><th>Policy</th><th>Status</th><th>Requested</th></tr></thead>
                  <tbody>
                    {requests.filter((r) => r.id !== activeDetail?.id).map((r) => (
                      <tr key={r.id}><td>{r.request_number}</td><td className="small">{r.policy_name}</td><td><Badge bg={requestStatusVariant(r.status)}>{r.status}</Badge></td><td className="small">{new Date(r.requested_at).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Card.Body>

      <SubmitTaskApprovalDialog show={showSubmit} onHide={() => setShowSubmit(false)} taskId={taskId} onSubmitted={load} />

      {decisionTarget && (
        <TaskApprovalDecisionDialog
          show={!!decisionTarget}
          onHide={() => setDecisionTarget(null)}
          requestId={activeDetail.id}
          stage={decisionTarget}
          taskTitle={activeDetail.task_title}
          onDecided={load}
        />
      )}

      <Modal show={!!delegateTarget} onHide={() => setDelegateTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delegate Approval Stage</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">Delegation affects only this approval stage — it does not change task assignment or task delegation.</div>
          <Form.Label className="small">Delegate To</Form.Label>
          <Form.Select value={delegateToUserId} onChange={(e) => setDelegateToUserId(e.target.value)}>
            <option value="">Select...</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Form.Select>
          <Form.Label className="small mt-2">Reason (optional)</Form.Label>
          <Form.Control value={delegateReason} onChange={(e) => setDelegateReason(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDelegateTarget(null)}>Cancel</Button>
          <Button variant="primary" onClick={submitDelegate}>Delegate</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
